import React from "react";
import type { ApolloClient } from "@apollo/client";
import type { ConnectedProps } from "react-redux";
import type { WithApolloClient } from "@apollo/client/react/hoc";
import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import IconTable from "components/IconTable";
import Alert from "components/Alert";
import GroupForm from "components/GroupForm";
import EditGroupForm from "components/EditGroupForm";
import BankLinkModal from "./BankLinkModal";
import IconTableHeader from "components/IconTableHeader";
import GroupMigrationWorkflow from "components/groupMigrationWorkflow/GroupMigrationWorkflow";

import { Form, Modal, OverlayTrigger, Popover } from "react-bootstrap";
import { filter, get, isEmpty, keyBy, map, size, some } from "lodash";
import { withApollo } from "@apollo/client/react/hoc";
import { connect } from "react-redux";
import {
  FiAlertTriangle,
  FiDollarSign,
  FiEdit3,
  FiUsers,
} from "react-icons/fi";
import { payrollPeriodsToEnglishMapping } from "utils/timeHelper";
import { getBanksAndGroups } from "actions/adminActions";
import {
  addEmployerGroup,
  currentPayrolls,
  deleteEmployerGroup,
  updateEmployerGroup,
  updateProgressiveOnboarding,
} from "actions/employerActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { daysBeforeDebitId, uniqueInviteCodesId } from "statics/featureIds";
import { adminConstants, employerConstants } from "actions/types";
import { hasEnabledPayrollIntegration } from "store/selectors/employer";
import { toast } from "react-toastify";
import {
  progressiveOnboardingEvents,
  progressiveOnboardingStates,
} from "statics/states";
import "./EmployerInfo.scss";
import { BsTrash } from "react-icons/bs";
import type { RootState } from "types/state";
import type {
  Company,
  EmployerGroup,
  PayrollPeriod,
  UserBank,
} from "types/models";
import type { IconTableColumn } from "components/IconTable";

// GroupForm/EditGroupForm are already-TS, but the call sites below pass extra
// props the components ignore (companyId / payPeriods — pre-existing dead
// props). Widen the component types here so those props type-check without an
// element-level suppression that would hide every other error on the element.
const GroupFormTyped = GroupForm as React.ComponentType<
  React.ComponentProps<typeof GroupForm> & { companyId?: string }
>;
const EditGroupFormTyped = EditGroupForm as React.ComponentType<
  React.ComponentProps<typeof EditGroupForm> & { payPeriods?: unknown }
>;

const groupActions = [
  employerConstants.ADD_EMPLOYER_GROUPS,
  employerConstants.DELETE_EMPLOYER_GROUPS,
  employerConstants.UPDATE_EMPLOYER_GROUPS,
  employerConstants.CURRENT_PAYROLLS,
];
const errorSelector = createErrorSelector([
  adminConstants.GET_BANKS_AND_GROUPS,
  employerConstants.CURRENT_PAYROLLS,
]);
const groupEditErrorSelector = createErrorSelector(
  employerConstants.UPDATE_EMPLOYER_GROUPS
);

const loadingSelector = createLoadingSelector([
  ...groupActions,
  adminConstants.GET_BANKS_AND_GROUPS,
]);
const groupErrorSelector = createErrorSelector([
  employerConstants.ADD_EMPLOYER_GROUPS,
  employerConstants.DELETE_EMPLOYER_GROUPS,
  employerConstants.CURRENT_PAYROLLS,
]);

interface OwnProps {
  companyId?: string;
  company: Company;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  return {
    error: errorSelector(state),
    groupCreationError: groupErrorSelector(state),
    groupEditError: groupEditErrorSelector(state),
    isLoading: loadingSelector(state),
    hasPayrollIntegration: hasEnabledPayrollIntegration({
      employer: { company: ownProps.company },
    } as unknown as RootState),
  };
};

const mapDispatchToProps = {
  getBanksAndGroups,
  addEmployerGroup,
  deleteEmployerGroup,
  updateEmployerGroup,
  currentPayrolls,
  updateProgressiveOnboarding,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

/** Success-action shape resolved by the getBanksAndGroups thunk. */
interface BanksAndGroupsResult {
  data: {
    getCompanyBanks: UserBank[];
    getCompanyGroups: EmployerGroup[];
  };
}

/** Row shape produced by _getGroupData and consumed by the group columns. */
interface GroupRow {
  id: string;
  name: string;
  planType?: string | null;
  processingDate: unknown;
  nextPayrollDate: unknown;
  payrollFrequencyLabel?: string;
  numberOfParticipants: number;
  numberOfPendingParticipants: number;
  accountId?: string | null;
  groupIsDeletable: boolean;
  group: EmployerGroup;
  groupId: string;
  companyGroupIndex?: number | null;
  groupHasProcessingPayrolls: boolean;
  groupHasApprovedPayrolls: boolean;
  groupHasPendingInvites: boolean;
}

/** Row shape produced by _getBankData and consumed by the bank columns. */
interface BankRow {
  id: string;
  accountId?: string | null;
  verificationStatus?: string | null;
  state?: string | null;
  bankName?: string | null;
  accountSubtype?: string | null;
}

interface State {
  initialFetching: boolean;
  banks: UserBank[];
  groups: EmployerGroup[];
  showGroupForm: boolean;
  groupForEdit: EmployerGroup | null;
  showGroupEditForm: boolean;
  showLinkModal: boolean;
  showMigrationModal: boolean;
  targetGroupIdForMove: string;
  payPeriods: PayrollPeriod[];
  showDeleteGroupPopover: Record<string, boolean>;
  // written by _migrateOnClick / render branches; not in the initial state object
  showMigration?: boolean;
}

class Groups extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      banks: [],
      groups: [],
      showGroupForm: false,
      groupForEdit: null,
      showGroupEditForm: false,
      showLinkModal: false,
      showMigrationModal: false,
      targetGroupIdForMove: "",
      payPeriods: [],
      showDeleteGroupPopover: {},
    };
  }

  async componentDidMount() {
    // dispatchFullLifecycle resolves to a success | failure action union; the
    // original code reads the success `.data` shape directly — cast to it.
    const {
      data: { getCompanyBanks: banks, getCompanyGroups: groups },
    } = (await this.props.getBanksAndGroups(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
    )) as unknown as BanksAndGroupsResult;
    const { data: payPeriods } = (await this.props.currentPayrolls(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
    )) as unknown as { data: PayrollPeriod[] };
    this.setState({ initialFetching: false, banks, groups, payPeriods });
  }

  _submitGroup = (group: Parameters<typeof addEmployerGroup>[2]) => {
    void this.props
      .addEmployerGroup(
        this.props.client as ApolloClient<object>,
        this.props.companyId as string,
        group
      )
      .then(async (result) => {
        if (result.success) {
          toast.success("Successfully added group.");
          const {
            data: { getCompanyBanks: banks, getCompanyGroups: groups },
          } = (await this.props.getBanksAndGroups(
            this.props.client as ApolloClient<object>,
            this.props.companyId as string
          )) as unknown as BanksAndGroupsResult;
          this.setState({
            banks,
            groups,
            showGroupForm: false,
          });
        }
      });
  };

  _submitGroupForEdit = (group: Parameters<typeof updateEmployerGroup>[1]) => {
    void this.props
      .updateEmployerGroup(
        this.props.client as ApolloClient<object>,
        group,
        this.props.companyId as string
      )
      .then(async (result) => {
        if (result.success) {
          const {
            data: { getCompanyBanks: banks, getCompanyGroups: groups },
          } = (await this.props.getBanksAndGroups(
            this.props.client as ApolloClient<object>,
            this.props.companyId as string
          )) as unknown as BanksAndGroupsResult;
          this.setState({
            banks,
            groups,
            groupForEdit: null,
            showGroupEditForm: false,
          });
          toast.success("Successfully updated group.");
        }
      });
  };

  _showLinkModal = () => {
    this.setState({ showLinkModal: true });
  };

  _closeLinkModal = () => {
    this.setState({ showLinkModal: false });
  };

  _removeGroup = (id: string) => {
    void this.props
      .deleteEmployerGroup(this.props.client as ApolloClient<object>, {
        id,
        companyId: this.props.companyId,
        migratedToGroupId: this.state.targetGroupIdForMove,
      })
      .then((result) => {
        if (result.success) {
          toast.success("Successfully removed group.");
          const newGroups = filter(
            this.state.groups,
            (group) => group.id !== id
          );
          this.setState({ groups: newGroups, showDeleteGroupPopover: {} });
        } else {
          toast.error(
            result.error || "There was a problem removing the group."
          );
        }
      });
  };

  _getGroupData = (): GroupRow[] => {
    const payrollsByGroupId = keyBy(this.state.payPeriods, "groupId");

    return map(this.state.groups, (group) => {
      const {
        name,
        id,
        associatedLinkRequests,
        planType,
        payrollFrequency,
        associatedBankAccount,
        associatedInvitations,
      } = group;

      const payrollFrequencyLabel =
        payrollPeriodsToEnglishMapping[
          payrollFrequency as keyof typeof payrollPeriodsToEnglishMapping
        ];
      const activeParticipants = filter(associatedLinkRequests, {
        status: "ACTIVE",
      });
      const pendingParticipants = filter(
        associatedLinkRequests,
        (request: { status?: string } | null) => {
          // preserve original: indexes .status directly (no null guard)
          return request!.status !== "ACTIVE";
        }
      );
      const numberOfParticipants = size(activeParticipants);
      const groupIsDeletable = isEmpty(associatedLinkRequests);
      const groupHasPendingInvites = !isEmpty(associatedInvitations);
      // found-not-fixed: associatedBankAccount is Maybe<UserBank>; .accountId
      // dereferences a possibly-null bank account (src/.../Groups.tsx)
      const accountId = associatedBankAccount!.accountId;

      const nextPayrollDate = get(payrollsByGroupId[id], "end");

      const processingDate = get(
        payrollsByGroupId[id],
        "payrollProcessingDate"
      );

      const approved = this.state.payPeriods.filter(
        (payroll) => payroll.status === "APPROVED"
      );
      const processing = this.state.payPeriods.filter(
        (payroll) => payroll.status === "PROCESSING"
      );
      const groupHasProcessingPayrolls = some(processing, {
        groupId: group.id,
      });
      const groupHasApprovedPayrolls = some(approved, { groupId: group.id });

      return {
        id,
        name,
        planType,
        processingDate,
        nextPayrollDate,
        payrollFrequencyLabel,
        numberOfParticipants,
        numberOfPendingParticipants: size(pendingParticipants),
        accountId,
        groupIsDeletable,
        group,
        groupId: group.id,
        companyGroupIndex: group.companyGroupIndex,
        groupHasProcessingPayrolls,
        groupHasApprovedPayrolls,
        groupHasPendingInvites,
      };
    });
  };

  _buildGroupColumns = (): Array<IconTableColumn<GroupRow>> => {
    const columns: Array<IconTableColumn<GroupRow>> = [
      {
        label: "Group Name",
        key: "name",
      },

      {
        label: "Plan Type",
        key: "planType",
      },
      {
        label: "Plan Code",
        customComponent: (props) => {
          const planCode = `${this.props.company.planId}-${props.companyGroupIndex}`;
          return planCode;
        },
      },
      {
        label: "Internal ID",
        key: "id",
      },

      {
        label: "Next Payroll Processing Date",
        key: "processingDate",
      },

      {
        label: "Next Payroll Date",
        key: "nextPayrollDate",
      },

      {
        label: "Payroll Frequency",
        key: "payrollFrequencyLabel",
      },
      {
        label: "# of active participants",
        key: "numberOfParticipants",
      },
      {
        label: "# of pending participants",
        key: "numberOfPendingParticipants",
      },

      {
        label: "Associated Bank",
        key: "accountId",
      },
      {
        customComponent: this._renderGroupActions,
      },
    ];
    return columns;
  };

  // Must keep a stable identity (class property, not an inline arrow in
  // _buildGroupColumns): IconTable renders this as an element type, so a new
  // function per render remounts the cell — and a freshly mounted
  // OverlayTrigger with show already true positions its popover against a
  // null target, which is invisible.
  _renderGroupActions = (props: GroupRow) => {
    const moreThanOneGroup = size(this.state.groups) > 1;
    return (
      <td className="action-icons">
        <span
          className="action-icon"
          onClick={() =>
            this.setState({
              showGroupEditForm: true,
              groupForEdit: props.group,
            })
          }
        >
          <FiEdit3 size={18} stroke="#96AAC4" fill="white" color="white" />
        </span>
        {props.groupIsDeletable && (
          <OverlayTrigger
            show={!!this.state.showDeleteGroupPopover[props.groupId]}
            onToggle={(nextShow) =>
              this.setState((prevState) => ({
                showDeleteGroupPopover: {
                  ...prevState.showDeleteGroupPopover,
                  [props.groupId]: nextShow,
                },
              }))
            }
            trigger="click"
            rootClose
            placement="bottom"
            overlay={
              <Popover className="action-popover">
                <Popover.Body onClick={(e) => e.stopPropagation()}>
                  {!props.groupHasApprovedPayrolls &&
                    !props.groupHasProcessingPayrolls && (
                      <p>Are you sure you want to remove this group?</p>
                    )}
                  {props.groupHasProcessingPayrolls && (
                    <div className="dismiss-payroll-info">
                      <p>
                        {"There are currently payroll contributions processing and you are unable to remove the group." +
                          " Come back in 1 business day, after contributions have processed, to remove the group."}
                      </p>
                    </div>
                  )}
                  {props.groupHasApprovedPayrolls &&
                    !props.groupHasProcessingPayrolls && (
                      <div className="dismiss-payroll-info">
                        <p>
                          {"There are currently payroll contributions approved for processing. By continuing and removing this group," +
                            " the payroll contributions will be un-approved. Would you like to continue?"}
                        </p>
                      </div>
                    )}
                  {this.props.hasPayrollIntegration && (
                    <div className="dismiss-payroll-info">
                      <p>
                        <strong>
                          Warning: Payroll integration is enabled. Engineering
                          may need to update configuration after removing this
                          group.
                        </strong>
                      </p>
                    </div>
                  )}
                  {props.groupHasPendingInvites && moreThanOneGroup && (
                    <div
                      className="pending-invites-warning"
                      style={{ marginBottom: 12 }}
                    >
                      <p>
                        This group has pending invites. You may optionally
                        select another group to transfer these invites to:
                      </p>
                      <Form.Select
                        name="targetGroupIdForMove"
                        value={this.state.targetGroupIdForMove}
                        onChange={(e) =>
                          this.setState({
                            targetGroupIdForMove: e.target.value,
                          })
                        }
                      >
                        <option value="">Select a group</option>
                        {this.state.groups
                          .filter((group) => group.id !== props.groupId)
                          .map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                      </Form.Select>
                    </div>
                  )}
                  <div>
                    <Button
                      type="button"
                      // @ts-expect-error Button does not declare an `action` prop; pre-existing inert prop the component ignores (not edited per shared-component rule)
                      action="cancel"
                      color="cancel"
                      btnLabel="Cancel"
                      onClick={() =>
                        this.setState({
                          showDeleteGroupPopover: {
                            ...this.state.showDeleteGroupPopover,
                            [props.groupId]: false,
                          },
                        })
                      }
                      size="sm"
                    />
                    {!props.groupHasProcessingPayrolls && (
                      <Button
                        type="button"
                        name="submit"
                        color="red"
                        btnLabel="Remove"
                        withArrow={true}
                        loading={this.props.isLoading}
                        onClick={() => this._removeGroup(props.groupId)}
                        disabled={props.groupHasProcessingPayrolls}
                        size="sm"
                      />
                    )}
                  </div>
                </Popover.Body>
              </Popover>
            }
          >
            <span className="action-icon">
              <BsTrash size={18} color="#B12121" />
            </span>
          </OverlayTrigger>
        )}
      </td>
    );
  };

  _finishAddingGroups = () => {
    void this.props.updateProgressiveOnboarding(
      this.props.client as ApolloClient<object>,
      progressiveOnboardingEvents.AddGroups,
      this.props.companyId as string
    );
  };

  _getBankData = (): BankRow[] => {
    return map(this.state.banks, (bank) => {
      const {
        id,
        accountId,
        verificationStatus,
        state,
        bankName,
        accountSubtype,
      } = bank;

      return {
        id,
        accountId,
        verificationStatus,
        state,
        bankName,
        accountSubtype,
      };
    });
  };

  _buildBankColumns = (): Array<IconTableColumn<BankRow>> => {
    const columns: Array<IconTableColumn<BankRow>> = [
      {
        label: "Bank Name",
        key: "bankName",
      },

      {
        label: "Account Type",
        key: "accountSubtype",
      },
      {
        label: "Bank Account #",
        key: "accountId",
      },

      {
        label: "Internal ID",
        key: "id",
      },

      {
        label: "Status",
        key: "verificationStatus",
      },

      {
        label: "State",
        key: "state",
      },
    ];
    return columns;
  };

  _migrateOnClick = () => {
    if (this.props.hasPayrollIntegration) {
      return this.setState({ showMigrationModal: true });
    } else {
      return this.setState({ showMigration: true });
    }
  };

  _buildGroupsSection = () => {
    if (isEmpty(this.state.groups)) {
      return (
        <div className="action-box">
          <div className="employerInfo">
            <div className="employerInfo-body">
              <div>
                <div className="circle">
                  <span className="icon">
                    <FiUsers color="white" stroke="#60A4BF" size={16} />
                  </span>
                </div>
              </div>
              <div className="employerInfo-intro">
                <IconHeader variant="labelHeader" headerText="Groups" />
                <IconSubheader subheader="Employer has not created a payroll group yet." />
              </div>
            </div>
            <div className="employerInfo-action">
              <Button
                disabled={isEmpty(this.state.banks)}
                onClick={() => this.setState({ showGroupForm: true })}
                size={"sm"}
                btnLabel={"Add Group"}
                name="action"
              />
            </div>
          </div>
        </div>
      );
    }

    const columns = this._buildGroupColumns();
    const data = this._getGroupData();

    const tableButton = (
      <Button
        onClick={this._migrateOnClick}
        color="primary"
        name="action"
        size="sm"
        btnLabel="Migrate Employees"
      />
    );

    return (
      <div className="action-box">
        <IconTableHeader
          tableHeader="Groups"
          tableCount={size(this.state.groups)}
          tableButton={tableButton}
        ></IconTableHeader>
        <IconTable columns={columns} data={data} />
        <div
          className="btn-row"
          style={{
            padding: 12,
          }}
        >
          <Button
            disabled={isEmpty(this.state.banks)}
            onClick={() => this.setState({ showGroupForm: true })}
            size={"sm"}
            btnLabel={"Add Group"}
            name="action"
          />
          {this.props.company.progressiveOnboardingState ===
            progressiveOnboardingStates.AddedBankAccount && (
            <Button
              onClick={this._finishAddingGroups}
              size={"sm"}
              btnLabel={"Finish Adding Groups"}
              name="action"
              color="action"
            />
          )}
        </div>
      </div>
    );
  };

  _buildBanksSection = () => {
    if (isEmpty(this.state.banks)) {
      return (
        <div className="action-box">
          <div className="employerInfo">
            <div className="employerInfo-body">
              <div>
                <div className="circle">
                  <span className="icon">
                    <FiDollarSign color="white" stroke="#60A4BF" size={16} />
                  </span>
                </div>
              </div>
              <div className="employerInfo-intro">
                <IconHeader variant="labelHeader" headerText="Bank" />
                <IconSubheader subheader="Employer has not linked a bank yet." />
              </div>
            </div>
          </div>
        </div>
      );
    }

    const columns = this._buildBankColumns();
    const data = this._getBankData();

    return (
      <div className="action-box">
        <IconTableHeader
          tableHeader="Bank"
          tableCount={size(this.state.banks)}
        />
        <IconTable columns={columns} data={data} />
      </div>
    );
  };

  _getGroupForm = () => {
    const hasExpiredInvites = some(
      this.props.company.enabledProductFeatures,
      (featureId) => featureId === uniqueInviteCodesId
    );
    const enabledDaysBeforeDebit = some(
      this.props.company.enabledProductFeatures,
      (featureId) => featureId === daysBeforeDebitId
    );
    return (
      <GroupFormTyped
        onClose={() => this.setState({ showGroupForm: false })}
        // GroupForm types onSubmit as (group: Record<string, unknown>) => void;
        // our handler takes the addEmployerGroup payload shape — bridge here.
        onSubmit={this._submitGroup as (group: Record<string, unknown>) => void}
        companyId={this.props.companyId}
        error={this.props.groupCreationError}
        // GroupForm's GroupAccount.accountId is non-null; UserBank.accountId is
        // string | null — cast at the boundary.
        accounts={
          this.state.banks as React.ComponentProps<typeof GroupForm>["accounts"]
        }
        isLoading={this.props.isLoading}
        // company.planTypes is Maybe<ProductFeature[]>; GroupForm wants
        // GroupPlanType[] ({ name }) — cast at the boundary (value unchanged).
        planTypes={
          this.props.company.planTypes as React.ComponentProps<
            typeof GroupForm
          >["planTypes"]
        }
        hasExpiredInvites={hasExpiredInvites}
        enabledDaysBeforeDebit={enabledDaysBeforeDebit}
      />
    );
  };
  _getEditGroupForm = () => {
    const hasExpiredInvites = some(
      this.props.company.enabledProductFeatures,
      (featureId) => featureId === uniqueInviteCodesId
    );
    return (
      <EditGroupFormTyped
        onClose={() => this.setState({ showGroupEditForm: false })}
        // EditGroupForm types onSubmit as (group: GroupSubmitValues) => void;
        // our handler takes the updateEmployerGroup payload — bridge here.
        onSubmit={
          this._submitGroupForEdit as React.ComponentProps<
            typeof EditGroupForm
          >["onSubmit"]
        }
        // EditGroupForm requires a non-null Group with associatedBankAccount.id
        // and schedule; state.groupForEdit is EmployerGroup | null — cast.
        group={
          this.state.groupForEdit as React.ComponentProps<
            typeof EditGroupForm
          >["group"]
        }
        payPeriods={this.state.payPeriods}
        error={this.props.groupEditError}
        accounts={this.state.banks}
        isLoading={this.props.isLoading}
        hasExpiredInvites={hasExpiredInvites}
      />
    );
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    if (this.props.error) {
      return <Alert msg={this.props.error} type={"error"} />;
    }

    if (this.state.showMigration) {
      return (
        <GroupMigrationWorkflow
          onCancel={() => this.setState({ showMigration: false })}
          onSuccess={(groups) => {
            this.setState({
              groups: groups as EmployerGroup[],
              showMigration: false,
            });
          }}
          // runtime passes an EmployerGroup[]; the component's `groups` prop is
          // typed Record<string, MigrationGroup>, but lodash find/map handle
          // both — cast at the boundary, preserving the original array.
          groups={this.state.groups as unknown as Record<string, never>}
          companyId={this.props.companyId}
        />
      );
    }

    if (this.state.showGroupForm) {
      return this._getGroupForm();
    }
    if (this.state.showGroupEditForm) {
      return this._getEditGroupForm();
    }
    if (this.state.showLinkModal) {
      return (
        <BankLinkModal
          companyId={this.props.companyId}
          onClose={this._closeLinkModal}
        />
      );
    }

    return (
      <div className="company-groups-banks">
        <div className="groups">
          {this.state.showMigrationModal && (
            <Modal
              centered
              scrollable
              show={true}
              // found-not-fixed: this._onClose is never defined on the class —
              // onHide receives undefined at runtime (pre-existing bug)
              // @ts-expect-error _onClose does not exist on Groups
              onHide={this._onClose}
              className="icon-modal"
            >
              <Modal.Header closeButton>
                <div className="payroll-integration-modal">
                  <div className="modal-circle">
                    <span className="icon">
                      <FiAlertTriangle
                        color="white"
                        stroke="#B12121"
                        size={16}
                      />
                    </span>
                  </div>

                  <p className="payroll-integration">
                    Payroll Integration Warning!
                  </p>
                </div>
              </Modal.Header>
              <Modal.Body>
                This employer has an active payroll integration. <br></br>
                <strong>DO NOT PROCEED</strong> with any group changes without
                consulting Engineering. Group changes will break the integration
                and require custom work.
              </Modal.Body>

              <Modal.Footer>
                <div className="submit-row">
                  <Button
                    btnLabel="Cancel"
                    color="cancel"
                    name="cancel"
                    type="button"
                    onClick={() => this.setState({ showMigrationModal: false })}
                  />
                  <Button
                    btnLabel="Continue"
                    name="submit"
                    color="red"
                    onClick={() =>
                      this.setState({
                        showMigration: true,
                        showMigrationModal: false,
                      })
                    }
                  />
                </div>
              </Modal.Footer>
            </Modal>
          )}
          {this._buildGroupsSection()}
        </div>
        <div className="banks">{this._buildBanksSection()}</div>
      </div>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withApollo<Props>(Groups));
